import React, { useState, useCallback, useEffect } from 'react';
import { Container } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import {
  EditOutlined,
  HighlightOff,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import useAuth from '../../../common/hooks/auth';
import useGeneralAppState from '../../../common/hooks/general-app-state';
import { config } from '../../../common/config/base';
import { listIntegrators } from '../../../common/api/integrators/list-integrators';
import {
  firstTableCellStyles,
  iconStyles,
  otherTableCellStyles,
  tableRowStyles,
} from '../styles/table';
import { confirmDialogInitialProps } from '../../../components/confirmation-dialog/initial-props';
import ConfirmationDialog from '../../../components/confirmation-dialog';
import { deleteIntegrator } from '../../../common/api/proposals/delete-integrator';

interface IntegratorListDto {
  id: string;
  name: string;
}

export function IntegratorTable() {
  const theme = useTheme();
  const { setAppMessage } = useGeneralAppState();
  const { user, handleLogout } = useAuth();
  const [integratorList, setIntegratorList] = useState<IntegratorListDto[]>([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationDialogProps, setConfirmationDialogProps] = useState(
    confirmDialogInitialProps,
  );

  const rowsPerPage = 5;
  const emptyRows = isLoading
    ? rowsPerPage
    : rowsPerPage - integratorList.length;

  const getIntegrators = useCallback(async () => {
    if (!user) return;
    const { token } = user;

    setIsLoading(true);
    const {
      success,
      message,
      data,
      count: integratorCount,
    } = await listIntegrators({
      token,
      page,
    });
    setIsLoading(false);

    if (success) {
      setCount(integratorCount);
      setIntegratorList(data);
      return;
    }
    setAppMessage({
      title: 'Error',
      content: message as string,
    });
    handleLogout();
  }, [handleLogout, setAppMessage, user, page]);

  useEffect(() => {
    getIntegrators();
  }, [getIntegrators, user]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleDelete = useCallback(
    async (integratorId: string) => {
      setConfirmationDialogProps(confirmDialogInitialProps);
      setIsLoading(true);
      const { success, message } = await deleteIntegrator({
        integratorId,
        token: user?.token as string,
      });
      setIsLoading(false);

      if (success) {
        getIntegrators();
        setAppMessage({
          title: 'Sucesso',
          content: 'O integrador foi deletado.',
        });
        return;
      }

      setAppMessage({
        title: 'Error',
        content: message as string,
      });
    },
    [user, setAppMessage, getIntegrators, setIsLoading],
  );

  const handleConfirmDelete = useCallback(
    (proposalId: string) => () => {
      setConfirmationDialogProps({
        isVisible: true,
        title: 'Atenção',
        message: 'Deletar integrador? Esta ação não pode ser revertida.',
        handleReject: () =>
          setConfirmationDialogProps(confirmDialogInitialProps),
        handleConfirm: () => handleDelete(proposalId),
      });
    },
    [handleDelete],
  );

  const tableHeaderSettings = {
    fontWeight: 800,
    color: theme.palette.primary.dark,
  };

  return (
    <Container
      sx={{
        paddingTop: '100px',
      }}
    >
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: '650px' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={firstTableCellStyles}>
                <Typography sx={tableHeaderSettings}>Integrador</Typography>
              </TableCell>
              <TableCell sx={otherTableCellStyles} align="center">
                <Typography sx={tableHeaderSettings}>Excluir</Typography>
              </TableCell>
              <TableCell sx={otherTableCellStyles} align="center">
                <Typography sx={tableHeaderSettings}>Editar</Typography>
              </TableCell>
              <TableCell sx={otherTableCellStyles} align="center">
                <Typography sx={tableHeaderSettings}>Visualizar</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading &&
              integratorList?.map((integrator) => (
                <TableRow
                  sx={{
                    height: `${tableRowStyles.height}px`,
                    '&:nth-of-type(odd)': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&:last-child td, &:last-child th': {
                      border: 0,
                    },
                  }}
                  key={integrator.id}
                >
                  <TableCell
                    sx={firstTableCellStyles}
                    component="th"
                    scope="row"
                  >
                    {integrator.name}
                  </TableCell>
                  <TableCell align="center">
                    {integrator.id !== user?.integratorId && (
                      <IconButton
                        sx={iconStyles}
                        onClick={handleConfirmDelete(integrator.id)}
                      >
                        <HighlightOff></HighlightOff>
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={otherTableCellStyles} align="center">
                    <IconButton
                      sx={iconStyles}
                      href={config.internalPaths.editIntegrator.replace(
                        ':integratorId',
                        integrator.id,
                      )}
                    >
                      <EditOutlined />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={otherTableCellStyles} align="center">
                    {integrator.id !== user?.integratorId && (
                      <IconButton
                        sx={iconStyles}
                        href={config.internalPaths.integratorProposal.replace(
                          ':integratorId',
                          integrator.id,
                        )}
                      >
                        <VisibilityOutlined />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {emptyRows > 0 && (
              <TableRow
                sx={{
                  height: `${tableRowStyles.height * emptyRows}px`,
                }}
              >
                <TableCell colSpan={7} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        count={count}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
        page={page}
        onPageChange={handleChangePage}
      ></TablePagination>
      <ConfirmationDialog {...confirmationDialogProps}></ConfirmationDialog>
    </Container>
  );
}
